import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';

import SendDetail from './SendDetail';
import RenderTdContent from './RenderTdContent';

import useGlobalStatus from '../../hook/useGlobalStatus';
import useReportStatus from '../../hook/useReportStatus';
import useColorSystem from '../../hook/useColorSystem';

import * as basicConstants from '../../constants/basic';
import * as urlConstants from '../../constants/url';
import * as mediaConstants from '../../constants/mediaData';

import { headCells } from './data/sendListTableConstants';

import ModalComponent from '../modal/ModalComponent';
import { MODAL_SEND_LIST } from '../modal/constants';
import Loading from '../loading/Loading';
import NoData from '../alert/NoData';

import { ButtonMain } from '../customComponent/button';
import { SearchInput } from '../customComponent/form';
import { LinkCustom } from '../CustomComp';

import { AddIcon, SearchIcon, CloseOutlinedIcon } from '../../constants/images';

import theme from '../../theme';


const SendListTable = (props) => {
  const intl = useIntl();
  const { media, fetchData, testDownloadOrSend, handleSendAllEmails, handleModalSubmit } = props;
  const { currentActiveData, setCurrentActiveData, locale, entryPath, setEntryPath, setModalContent, isModalOpen, setIsModalOpen, setModalData } = useGlobalStatus();
  const { colorMedia, mainColor, lightColor, darkColor, w01Color } = useColorSystem();
  const {
    searchKeyword,
    setSearchKeyword,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    sendList,
    sendListLength,
    isLoading: isLoadingData,
    setIsLoading: setIsLoadingData,
    isProcessing
  } = useReportStatus();

  const [showDataDetail, setShowDataDetail] = useState(false);
  const [modalName, setModalName] = useState('');

  const handelCancelSearch = () => {
    setSearchKeyword('');
  }

  const handleSearchInput = (e) => {
    const { value } = e.target;
    setSearchKeyword(value);
    setPage(0);
  }

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
    setIsLoadingData(true);
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setIsLoadingData(true);
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setIsLoadingData(true);
  }

  const deleteData = (data) => {
    const { weeklyReportId, weeklyReportName } = data;
    setModalData({ weeklyReportId: weeklyReportId, weeklyReportName: weeklyReportName });
    setModalContent({ ...MODAL_SEND_LIST['deleteReport'], media: media });
    setIsModalOpen(true);
  }

  return (
    <>
      <Typography variant="h2"><FormattedMessage id="sendList.header.title" values={{ media: mediaConstants.MEDIA_DATA[media].media }} /></Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <LinkCustom to={mediaConstants.MEDIA_DATA[media].createDataUrl}>
          <ButtonMain
            color={colorMedia}
            variant="contained"
            sx={{ color: '#fff' }}
            onClick={() => {
              setEntryPath(mediaConstants.MEDIA_DATA[media].createDataUrl);
            }}
          >
            <AddIcon />
            <Typography
              sx={{
                display: {
                  sm: 'inline-block',
                  xs: 'none'
                }
              }}
            >
              <FormattedMessage id="sendList.button.createReport" />
            </Typography>
          </ButtonMain>
        </LinkCustom>

        <SearchInput
          id="search"
          media={media}
          placeholder={intl.formatMessage({ id: "sendList.search.placeholder" })}
          startAdornment={<InputAdornment position="start"><SearchIcon /></InputAdornment>}
          endAdornment={
            searchKeyword && (
              <InputAdornment position="end">
                <CloseOutlinedIcon onClick={handelCancelSearch} />
              </InputAdornment>
            )}
          sx={{
            width: {
              xs: `calc(100% - 72px)`,
              sm: '50%',
              md: '40%'
            },
            cursor: 'pointer'
          }}
          value={searchKeyword}
          onChange={handleSearchInput}
        />
      </Box>

      <TableContainer
        sx={{
          position: 'relative',
          height: basicConstants.TABLE_ROW_HEIGHT * basicConstants.TABLE_BASIC_ROW_NUM,
          minHeight: basicConstants.TABLE_ROW_HEIGHT * basicConstants.TABLE_BASIC_ROW_NUM,
          maxHeight: basicConstants.TABLE_ROW_HEIGHT * basicConstants.TABLE_BASIC_ROW_NUM,
          marginTop: 4,
          borderRadius: '4px',
          border: `1px solid ${theme.palette.grey.w300}`,
          borderColor: (sendList.length > 0 || searchKeyword || isLoadingData) ? theme.palette.grey.w300 : 'transparent'
        }}
      >
        {/* 渲染表頭 */}
        {
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {
                  headCells.map((headCell, index) => {
                    return (
                      <TableCell
                        key={headCell.id}
                        align={headCell.align}
                        sortDirection={orderBy === headCell.orderName ? order : false}
                        size="small"
                        sx={{
                          width: headCell.width ? headCell.width : 'auto',
                          padding: '8px 16px',
                          backgroundColor: theme.palette.grey.w200
                        }}
                      >
                        {
                          headCell.sortable ? (
                            <TableSortLabel
                              active={orderBy === headCell.orderName}
                              direction={orderBy === headCell.orderName ? order : 'asc'}
                              onClick={createSortHandler(headCell.orderName)}
                            >
                              <FormattedMessage id={headCell.displayName} />
                            </TableSortLabel>
                          ) : (
                            <FormattedMessage id={headCell.displayName} />
                          )
                        }
                      </TableCell>
                    )
                  })
                }
              </TableRow>
            </TableHead>

            <TableBody>
              {
                sendList.length > 0 &&
                sendList.map((row, index) => {
                  return (
                    <TableRow
                      key={row.weeklyReportId}
                      hover
                    >
                      {
                        headCells.map((cell, index) => {
                          return (
                            <TableCell
                              key={`${cell.id}${index}`}
                              align={cell.align}
                              size="small"
                              sx={{
                                minWidth: cell.width ? cell.width : 'initial',
                                maxWidth: cell.maxWidth ? cell.maxWidth : 'initial',
                                padding: '12px 16px'
                              }}
                              onClick={() => {
                                if (cell.id === 'weeklyReportName') {
                                  setCurrentActiveData(row);
                                  setShowDataDetail(true);
                                }
                              }}
                            >
                              <RenderTdContent
                                media={media}
                                id={cell.id}
                                row={row ? row : null}
                                deleteData={deleteData}
                                setIsLoadingData={setIsLoadingData}
                                setModalName={setModalName}
                                fetchData={fetchData}
                                setPage={setPage}
                              />
                            </TableCell>
                          )
                        })
                      }
                    </TableRow>
                  )
                })
              }
            </TableBody>
          </Table>
        }
        {
          !sendList.length && !isLoadingData &&
          <NoData searchKeyword={searchKeyword} media={media}>
            <>
              <Typography mb={1}><FormattedMessage id="sendList.table.noData" /></Typography>
              <LinkCustom to={mediaConstants.MEDIA_DATA[media].createDataUrl}>
                <ButtonMain
                  color={colorMedia}
                  variant="contained"
                  onClick={() => {
                    setEntryPath(mediaConstants.MEDIA_DATA[media].createDataUrl);
                  }}
                >
                  <FormattedMessage id="sendList.button.createNow" />
                </ButtonMain>
              </LinkCustom>
            </>
          </NoData>
        }
      </TableContainer>
      {
        sendList.length > 0 &&
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={sendListLength}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
        />
      }
      {
        currentActiveData &&
        <SendDetail
          media={media}
          currentData={currentActiveData}
          showDataDetail={showDataDetail}
          setShowDataDetail={setShowDataDetail}
          isProcessing={isProcessing}
          handleDownloadOrSend={testDownloadOrSend}
          handleSendAllEmails={handleSendAllEmails}
        />
      }
      <ModalComponent
        isLoading={isProcessing}
        handleModalSubmit={handleModalSubmit}
      />

      <Loading isLoading={isLoadingData} variant='inside' />
    </>
  )
}

export default SendListTable;